import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  productFamilyConstitutionApi,
  type CertificationGate,
  type ComplianceEvaluation,
  type ComplianceKPI,
  type ConstitutionDashboard,
  type ConstitutionStatus,
  type GuidingPrinciple,
  type ModuleManifestRequirements,
  type PlatformDependency,
  type ProductFamilyEntry,
  type StandardLayer,
} from '../../api/product-family-constitution';

type Tab = 'overview' | 'principles' | 'structure' | 'registry' | 'gates' | 'kpis' | 'manifest' | 'status';
type Theme = 'light' | 'dark';

function statusColor(status: string, dark: boolean) {
  if (status === 'compliant') return dark ? 'text-emerald-400' : 'text-emerald-600';
  if (status === 'partial') return dark ? 'text-amber-400' : 'text-amber-600';
  if (status === 'planned') return dark ? 'text-sky-400' : 'text-sky-600';
  if (status === 'non_compliant') return dark ? 'text-red-400' : 'text-red-600';
  return dark ? 'text-slate-400' : 'text-slate-500';
}

export function ProductFamilyConstitutionPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<ConstitutionDashboard | null>(null);
  const [principles, setPrinciples] = useState<GuidingPrinciple[]>([]);
  const [layers, setLayers] = useState<StandardLayer[]>([]);
  const [deps, setDeps] = useState<PlatformDependency[]>([]);
  const [families, setFamilies] = useState<ProductFamilyEntry[]>([]);
  const [gates, setGates] = useState<CertificationGate[]>([]);
  const [kpis, setKpis] = useState<ComplianceKPI[]>([]);
  const [manifestReqs, setManifestReqs] = useState<ModuleManifestRequirements | null>(null);
  const [status, setStatus] = useState<ConstitutionStatus | null>(null);
  const [evalResult, setEvalResult] = useState<ComplianceEvaluation | null>(null);
  const [selectedFamily, setSelectedFamily] = useState<string>('pf-planning');
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, pr, ly, dp, fam, gt, kp, mr, st] = await Promise.all([
        productFamilyConstitutionApi.dashboard(),
        productFamilyConstitutionApi.principles(),
        productFamilyConstitutionApi.layers(),
        productFamilyConstitutionApi.platformDependencies(),
        productFamilyConstitutionApi.registry(),
        productFamilyConstitutionApi.certificationGates(),
        productFamilyConstitutionApi.kpis(),
        productFamilyConstitutionApi.manifestRequirements(),
        productFamilyConstitutionApi.status(),
      ]);
      setDashboard(dash);
      setPrinciples(pr);
      setLayers(ly);
      setDeps(dp);
      setFamilies(fam);
      setGates(gt);
      setKpis(kp);
      setManifestReqs(mr);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load product family constitution');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const evaluateFamily = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await productFamilyConstitutionApi.evaluate(selectedFamily);
      setEvalResult(result);
      setActionMsg(`Compliance evaluation: ${result.status} (${result.passed_gates}/${result.total_gates} gates)`);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Evaluation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Executive Summary' },
    { id: 'principles', label: 'Guiding Principles' },
    { id: 'structure', label: 'Product Structure' },
    { id: 'registry', label: 'Product Families' },
    { id: 'gates', label: 'Certification Gates' },
    { id: 'kpis', label: 'Compliance KPIs' },
    { id: 'manifest', label: 'Module Manifest' },
    { id: 'status', label: 'Constitution Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-rose-500">P00060</p>
          <h1 className="text-2xl font-semibold">Enterprise Product Family Constitution</h1>
          <p className={`text-sm ${muted}`}>
            Mandatory governance standard for every enterprise product family — modular, plug-and-play, AI-native and commercially licensable
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/foundation-certification" className="text-rose-500 hover:underline">Foundation Certification</Link>
            <span className={muted}>·</span>
            <Link to="/platform/master-constitution" className="text-rose-500 hover:underline">Master Constitution</Link>
            <span className={muted}>·</span>
            <Link to="/governance" className="text-rose-500 hover:underline">Governance Platform</Link>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setTheme(dark ? 'light' : 'dark')}
          className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}
        >
          {dark ? 'Light mode' : 'Dark mode'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2 dark:border-slate-800">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-rose-600 text-white' : `${card} ${muted}`}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Constitution', value: dashboard.constitution_id },
            { label: 'Product Families', value: dashboard.product_families },
            { label: 'Certified', value: dashboard.certified_families },
            { label: 'Avg Compliance', value: `${dashboard.avg_compliance_score.toFixed(1)}%` },
            { label: 'Principles', value: dashboard.principles },
            { label: 'Certification Gates', value: dashboard.certification_gates },
            { label: 'Version', value: dashboard.version },
            { label: 'Status', value: 'Mandatory Active' },
          ].map((item) => (
            <div key={item.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{item.label}</p>
              <p className="mt-1 text-2xl font-semibold">{item.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'principles' && (
        <div className="grid gap-3 md:grid-cols-2">
          {principles.map((p) => (
            <div key={p.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-medium">{p.name}</h3>
                {p.mandatory && <span className="text-xs text-rose-500">Mandatory</span>}
              </div>
              <p className={`mt-1 text-sm ${muted}`}>{p.description}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'structure' && (
        <div className="space-y-6">
          <div>
            <h2 className="mb-3 text-lg font-semibold">Standard Product Layers</h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {layers.map((l) => (
                <div key={l.key} className={`rounded-xl border p-4 ${card}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">{l.name}</h3>
                    {l.required && <span className="text-xs text-rose-500">Required</span>}
                  </div>
                  <p className={`mt-1 text-sm ${muted}`}>{l.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="mb-3 text-lg font-semibold">Mandatory Platform Dependencies</h2>
            <div className="overflow-x-auto rounded-xl border">
              <table className={`min-w-full text-sm ${card}`}>
                <thead>
                  <tr className={`border-b ${dark ? 'border-slate-800' : 'border-slate-200'}`}>
                    <th className="px-4 py-2 text-left">DEV ID</th>
                    <th className="px-4 py-2 text-left">Platform</th>
                    <th className="px-4 py-2 text-left">Service</th>
                    <th className="px-4 py-2 text-left">Port</th>
                  </tr>
                </thead>
                <tbody>
                  {deps.map((d) => (
                    <tr key={d.dev_id} className={`border-b ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                      <td className="px-4 py-2 font-mono text-xs">{d.dev_id}</td>
                      <td className="px-4 py-2">{d.name}</td>
                      <td className="px-4 py-2">{d.service}</td>
                      <td className="px-4 py-2">{d.port}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'registry' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className={`block text-xs ${muted}`}>Evaluate product family</label>
              <select
                value={selectedFamily}
                onChange={(e) => setSelectedFamily(e.target.value)}
                className={`mt-1 rounded-lg border px-3 py-2 text-sm ${card}`}
              >
                {families.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={evaluateFamily}
              className="rounded-lg bg-rose-600 px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              {busy ? 'Evaluating…' : 'Evaluate Compliance'}
            </button>
          </div>
          {evalResult && (
            <div className={`rounded-xl border p-4 ${card}`}>
              <p className={`font-medium ${statusColor(evalResult.status, dark)}`}>
                {evalResult.product_family_id}: {evalResult.status} — {evalResult.score.toFixed(1)}% ({evalResult.passed_gates}/{evalResult.total_gates} gates)
              </p>
              {evalResult.gaps && evalResult.gaps.length > 0 && (
                <ul className={`mt-2 list-inside list-disc text-sm ${muted}`}>
                  {evalResult.gaps.map((g) => <li key={g}>{g}</li>)}
                </ul>
              )}
            </div>
          )}
          <div className="overflow-x-auto rounded-xl border">
            <table className={`min-w-full text-sm ${card}`}>
              <thead>
                <tr className={`border-b ${dark ? 'border-slate-800' : 'border-slate-200'}`}>
                  <th className="px-4 py-2 text-left">Product Family</th>
                  <th className="px-4 py-2 text-left">DEV ID</th>
                  <th className="px-4 py-2 text-left">Lifecycle</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-right">Modules</th>
                  <th className="px-4 py-2 text-right">Score</th>
                  <th className="px-4 py-2 text-center">Certified</th>
                </tr>
              </thead>
              <tbody>
                {families.map((f) => (
                  <tr key={f.id} className={`border-b ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                    <td className="px-4 py-2 font-medium">{f.name}</td>
                    <td className="px-4 py-2 font-mono text-xs">{f.dev_id || '—'}</td>
                    <td className="px-4 py-2">{f.lifecycle_stage.replace(/_/g, ' ')}</td>
                    <td className={`px-4 py-2 ${statusColor(f.status, dark)}`}>{f.status}</td>
                    <td className="px-4 py-2 text-right">{f.modules}</td>
                    <td className="px-4 py-2 text-right">{f.compliance_score > 0 ? f.compliance_score.toFixed(1) : '—'}</td>
                    <td className="px-4 py-2 text-center">{f.certified ? '✓' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'gates' && (
        <div className="grid gap-3 md:grid-cols-2">
          {gates.map((g) => (
            <div key={g.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-medium">{g.name}</h3>
                <span className="text-xs uppercase text-rose-500">{g.category}</span>
              </div>
              <p className={`mt-1 text-sm ${muted}`}>{g.description}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'kpis' && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {kpis.map((k) => {
            const pct = k.unit === 'pct' ? k.value : null;
            const onTarget = k.value >= k.target;
            return (
              <div key={k.key} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{k.name}</p>
                <p className={`mt-1 text-2xl font-semibold ${onTarget ? (dark ? 'text-emerald-400' : 'text-emerald-600') : (dark ? 'text-amber-400' : 'text-amber-600')}`}>
                  {pct !== null ? `${k.value.toFixed(1)}%` : k.value}
                </p>
                <p className={`text-xs ${muted}`}>Target: {k.unit === 'pct' ? `${k.target}%` : k.target}</p>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'manifest' && manifestReqs && (
        <div className="grid gap-6 md:grid-cols-2">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Required Manifest Fields</h3>
            <ul className={`mt-2 space-y-1 text-sm ${muted}`}>
              {manifestReqs.required_fields.map((f) => (
                <li key={f} className="font-mono text-xs">{f}</li>
              ))}
            </ul>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Optional Manifest Fields</h3>
            <ul className={`mt-2 space-y-1 text-sm ${muted}`}>
              {manifestReqs.optional_fields.map((f) => (
                <li key={f} className="font-mono text-xs">{f}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-6 ${card}`}>
          <div className="grid gap-4 md:grid-cols-2">
            {Object.entries(status).map(([key, value]) => (
              <div key={key}>
                <p className={`text-xs uppercase ${muted}`}>{key.replace(/_/g, ' ')}</p>
                <p className="mt-1 font-medium">{String(value)}</p>
              </div>
            ))}
          </div>
          {dashboard?.extension_points && dashboard.extension_points.length > 0 && (
            <div className="mt-6">
              <h3 className="font-medium">Next Extension Points</h3>
              <ul className={`mt-2 space-y-2 text-sm ${muted}`}>
                {dashboard.extension_points.map((ep) => (
                  <li key={ep.dev_id}>
                    <span className="font-mono text-xs text-rose-500">{ep.dev_id}</span> — {ep.name}: {ep.description}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
